import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AuthGuard } from "../App"; // Assuming AuthGuard is correctly implemented
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTools, faTrashAlt, faCircle, faPlusCircle } from "@fortawesome/free-solid-svg-icons"; // Added relevant icons

const MaintenanceRequestsPage = () => {
  const [maintenanceRequests, setMaintenanceRequests] = useState([]);
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const userDataString = localStorage.getItem("user");
  const userData = JSON.parse(userDataString);
  const userId = userData.unique;

  const fetchMaintenanceRequests = () => {
    if (!token || !userId) {
      toast.error("Authentication details missing. Please log in again.");
      navigate("/"); // Redirect to login page
      return;
    }

    axios
      .get(
        `https://backend.inyumba.co.ke/iNyumba/maintenance/users/${userId}/maintenance-requests`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        // Ensure response.data.response is an array, handle empty cases
        setMaintenanceRequests(Array.isArray(response.data.response) ? response.data.response : []);
      })
      .catch((error) => {
        console.error("Error fetching maintenance requests:", error);
        if (error.response && error.response.data && error.response.data.response) {
          toast.error(error.response.data.response);
        } else {
          toast.error("Failed to load maintenance requests. Please try again.");
        }
      });
  };

  useEffect(() => {
    fetchMaintenanceRequests();
  }, [token, userId]); // Re-fetch if token or userId changes

  const handleDeleteRequest = (maintenanceRequestId) => {
    if (!window.confirm("Are you sure you want to delete this maintenance request?")) {
      return;
    }

    axios
      .delete(
        `https://backend.inyumba.co.ke/iNyumba/maintenance/maintenance-requests/${maintenanceRequestId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        toast.success(response.data.response || "Maintenance request deleted successfully!");
        // Filter out the deleted request from the state to update UI
        setMaintenanceRequests((prevRequests) =>
          prevRequests.filter((request) => request.request_id !== maintenanceRequestId)
        );
      })
      .catch((error) => {
        console.error("Error deleting maintenance request:", error);
        if (error.response && error.response.data && error.response.data.response) {
          toast.error(error.response.data.response);
        } else {
          toast.error("Failed to delete maintenance request. Please try again.");
        }
      });
  };

  const handleAddRequest = () => {
    navigate("/user/add-maintenance-request"); // Navigate to the add maintenance request page
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Pending":
        return "bg-yellow-100 text-yellow-800";
      case "Completed":
        return "bg-green-100 text-green-800";
      case "In Progress":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <AuthGuard>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 sm:p-6 lg:p-8">
      
        <div className="max-w-4xl mx-auto py-8">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-4xl font-extrabold text-gray-900">
              Your Maintenance Requests
            </h1>
            <button
              onClick={handleAddRequest}
              className="flex items-center space-x-2 bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 rounded-lg text-lg font-medium shadow-md transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              <FontAwesomeIcon icon={faPlusCircle} />
              <span>Add New Request</span>
            </button>
          </div>

          <p className="text-gray-600 text-lg mb-6">
            Track the status of your reported issues and property maintenance requests.
          </p>

          <div className="flex justify-between items-center mb-8">
            <span className="text-indigo-600 text-2xl font-bold">
              <FontAwesomeIcon icon={faTools} className="mr-3" />
              Today: {new Date().toLocaleDateString("en-US", { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </span>
          </div>

          {maintenanceRequests.length === 0 ? (
            <div className="bg-white rounded-xl shadow-lg p-8 text-center text-gray-600 text-xl">
              <p>You currently have no maintenance requests.</p>
              <p className="mt-4">Click "Add New Request" to create one.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {maintenanceRequests.map((request) => (
                <div key={request.request_id} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 space-y-3 flex flex-col justify-between">
                  <div>
                    <h3 className="text-gray-800 font-semibold text-xl mb-2">Request Details</h3>
                    <p className="text-gray-700 text-base leading-relaxed">
                      {request.request_content}
                    </p>
                  </div>
                  <div className="flex items-center justify-between mt-4">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(request.status)}`}>
                      <FontAwesomeIcon icon={faCircle} className="mr-2 text-xs" />
                      {request.status}
                    </span>
                    <button
                      onClick={() => handleDeleteRequest(request.request_id)}
                      className="flex items-center space-x-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                    >
                      <FontAwesomeIcon icon={faTrashAlt} />
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AuthGuard>
  );
};

export default MaintenanceRequestsPage;